// TenantLogin.jsx
// Updated for multi-tenant: any tenant added to a property can log in
// if *any* tenant at that property holds an active Stripe subscription.

import React, { useState } from 'react';
import {
    Grid, TextField, Button, Typography, Box, Paper,
    CircularProgress, Alert, Divider, Link,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import TenantLoginStyles from './TenantLogin.styles';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const STRIPE_SERVER_URL = process.env.REACT_APP_STRIPE_SERVER_URL;
const SEAM_SERVER_URL = process.env.REACT_APP_SEAM_SERVER_URL;

if (!STRIPE_KEY) console.error('❌ [CONFIG] REACT_APP_STRIPE_PUBLISHABLE_KEY is not defined');
const stripePromise = loadStripe(STRIPE_KEY);

const TenantLogin = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState('');
    const [propertyId, setPropertyId] = useState('');
    const [accessCode, setAccessCode] = useState('');

    // 'email' → 'subscribe' → 'verify'  OR  'email' → 'verify' (co-tenant path)
    const [mode, setMode] = useState('email');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Track whether this login is the subscriber or a co-tenant
    const [isSubscriber, setIsSubscriber] = useState(false);

    React.useEffect(() => {
        if (searchParams.get('success') === 'true') {
            setSuccess('Subscription successful! You now have access to your property.');
            setMode('verify');
        }
        if (searchParams.get('cancelled') === 'true') {
            setError('Subscription was cancelled. Please try again.');
        }
    }, [searchParams]);

    // const isDemo = searchParams.get('demo') === 'true';

    // ── Step 1: Check if email is a registered tenant for this property ────────
    // Then check if the property has any active subscription.
    const checkAccess = async () => {
        if (!email || !propertyId) {
            setError('Please enter your email and property ID');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const tenantSnap = await getDocs(
                collection(db, 'access_users', propertyId, 'tenant-emails')
            );
            const tenantDocs = tenantSnap.docs;
            const tenantEmails = tenantDocs.map(d => d.id.toLowerCase());
            const normalizedEmail = email.toLowerCase().trim();

            if (!tenantEmails.includes(normalizedEmail)) {
                setError('No tenant account found for this email and property ID.');
                setLoading(false);
                return;
            }

            const tenantDoc = tenantDocs.find(d => d.id.toLowerCase() === normalizedEmail);

            // ── Revocation check ───────────────────────────────────────────────
            const tenantStatus = tenantDoc?.data()?.status;
            if (tenantStatus === false) {
                setError('Your access has been revoked. Please contact your property manager.');
                setLoading(false);
                return;
            }

            // ── Lease end date check ───────────────────────────────────────────
            const leaseEndDate = tenantDoc?.data()?.leaseEndDate;
            if (leaseEndDate) {
                const leaseEnd = leaseEndDate?.toDate
                    ? leaseEndDate.toDate().toISOString().split('T')[0]
                    : String(leaseEndDate).split('T')[0];
                const today = new Date().toISOString().split('T')[0];
                if (today >= leaseEnd) {
                    setError(
                        today === leaseEnd
                            ? 'Your lease ends today. Access has been disabled. Please contact your property manager.'
                            : 'Your lease has expired. Please contact your property manager.'
                    );
                    setLoading(false);
                    return;
                }
            }

            // ── Set tenantId once all checks pass ──────────────────────────────
            localStorage.setItem('tenantId', normalizedEmail);
            localStorage.setItem('tenantPropertyId', propertyId);

            const mySubRes = await fetch(
                `${STRIPE_SERVER_URL}/api/subscribe/tenant/status?tenantEmail=${encodeURIComponent(normalizedEmail)}`
            );
            const mySub = await mySubRes.json();
            console.log("111", mySub, normalizedEmail);
            if (mySub.hasSubscription && mySub.status === 'active') {
                localStorage.setItem('tenantIsSubscriber', 'true');
                setIsSubscriber(true);
                setSuccess('Active subscription found! Enter your access code below.');
                setMode('verify');
                setLoading(false);
                return;
            }

            const propertyHasSub = await checkPropertySubscription(tenantEmails);

            if (propertyHasSub) {
                localStorage.setItem('tenantIsSubscriber', 'false');
                setIsSubscriber(false);
                setSuccess('Your property has an active subscription. Enter your access code below.');
                setMode('verify');
            } else {
                await notifyTenantsAboutSubscription(tenantEmails, propertyId);
                setMode('subscribe');
            }
        } catch (err) {
            console.error('Error checking access:', err);
            setError('Failed to check access. Please try again.');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('tenantPropertyId');
        } finally {
            setLoading(false);
        }
    };

    // ── Check if any tenant on the property has an active subscription ─────────
    const checkPropertySubscription = async (tenantEmails) => {
        const results = await Promise.allSettled(
            tenantEmails.map(te =>
                fetch(`${STRIPE_SERVER_URL}/api/subscribe/tenant/status?tenantEmail=${encodeURIComponent(te)}`)
                    .then(r => r.json())
            )
        );

        return results.some(r =>
            r.status === 'fulfilled' &&
            r.value?.hasSubscription &&
            r.value?.status === 'active'
        );
    };

    // ── Notify all tenants that no one has a subscription yet ─────────────────
    const notifyTenantsAboutSubscription = async (tenantEmails, propId) => {
        try {
            const emailServerUrl = process.env.REACT_APP_EMAIL_SERVER_URL ||
                (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://ai-realtor.onrender.com');

            await fetch(`${emailServerUrl}/notify-tenants-no-subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenantEmails,
                    propertyId: propId,
                    loginEmail: email,
                }),
            });
        } catch (err) {
            // Non-blocking: notification failure shouldn't block the login UI
            console.warn('Failed to send subscription notification emails:', err);
        }
    };

    // ── Start Stripe checkout ──────────────────────────────────────────────────
    const handleSubscribe = async () => {
        if (!email || !propertyId) {
            setError('Please enter your email and property ID');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${STRIPE_SERVER_URL}/api/subscribe/tenant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenantEmail: email,
                    propertyId,
                    managerId: '',
                }),
            });
            const data = await response.json();

            if (data.sessionId) {
                const stripe = await stripePromise;
                const { error: stripeErr } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
                if (stripeErr) setError(stripeErr.message);
            } else if (data.url) {
                window.location.href = data.url;
            } else {
                setError(data.error || 'Failed to start checkout');
            }
        } catch (err) {
            setError(`Failed to start subscription: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ── Verify access code (unchanged) ────────────────────────────────────────
    const handleVerifyCode = async () => {
        if (!accessCode || !propertyId) {
            setError('Please enter your access code');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${SEAM_SERVER_URL}/api/tenant/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code: accessCode,
                    propertyId,
                    email: localStorage.getItem('tenantId'), // already normalized and set
                }),
            });
            const data = await response.json();

            if (data.valid) {
                setSuccess('Access code verified! Redirecting...');
                // tenantId and propertyId already in localStorage — no need to set again
                setTimeout(() => navigate('/tenant-dashboard'), 1500);
            } else {
                setError(data.reason || 'Invalid access code');
            }
        } catch (err) {
            setError('Failed to verify access code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Grid container sx={TenantLoginStyles.container}>
            <Paper elevation={10} sx={TenantLoginStyles.paper}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/logo-new.png`}
                        alt="Tenant Guided Tours"
                        sx={TenantLoginStyles.logo}
                    />
                    <Typography variant="h5" sx={TenantLoginStyles.title}>Tenant Portal</Typography>
                    <Typography variant="body2" sx={TenantLoginStyles.subtitle}>
                        Access your property with smart lock codes
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={TenantLoginStyles.errorAlert} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={TenantLoginStyles.successAlert}>{success}</Alert>
                    )}

                    {/* ── Email + property form (shown in both 'email' and 'subscribe' modes) ── */}
                    {(mode === 'email' || mode === 'subscribe') && (
                        <Box
                            component="form"
                            sx={TenantLoginStyles.form}
                            onSubmit={e => { e.preventDefault(); checkAccess(); }}
                        >
                            <TextField
                                required fullWidth label="Email Address" type="email"
                                value={email} onChange={e => setEmail(e.target.value)}
                                sx={TenantLoginStyles.textField}
                                InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'gray' }}>📧</Box> }}
                            />
                            <TextField
                                required fullWidth label="Property ID"
                                value={propertyId} onChange={e => setPropertyId(e.target.value)}
                                sx={TenantLoginStyles.textField}
                                placeholder="Enter your property ID"
                                InputProps={{ startAdornment: <HomeIcon sx={{ mr: 1, color: 'gray' }} /> }}
                            />

                            {mode === 'email' && (
                                <Button type="submit" fullWidth variant="contained"
                                    sx={TenantLoginStyles.primaryButton} disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Check Access'}
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* ── Subscribe section ──────────────────────────────────── */}
                    {mode === 'subscribe' && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No active subscription found for this property.
                                One tenant subscribes and everyone on the property gets access.
                                All tenants have been notified by email.
                            </Alert>
                            <Box sx={TenantLoginStyles.infoBox}>
                                <Typography variant="body2" color="textSecondary">
                                    Get access to your property's smart lock
                                </Typography>
                                <Typography sx={TenantLoginStyles.priceHighlight}>
                                    $5.99
                                    <Typography component="span" variant="body2" color="textSecondary">
                                        /month
                                    </Typography>
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold', color: 'text.primary' }}>
                                    Subscription Includes
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'left' }}>
                                    <strong>• One-Time Access Codes</strong>
                                    <br />
                                    Secure, temporary passcodes for guests, house sitters, vendors, or anyone needing short-term access.
                                    <br /><br />
                                    <strong>• Benefit Hub Membership</strong>
                                    <br />
                                    Built-in savings on restaurants, travel, hardware stores, and major retailers.
                                    <br /><br />
                                    <strong>• Doorbell Camera Info (Where Available)</strong>
                                    <br />
                                    Device details and direct access links for homes with doorbell cameras.
                                </Typography>
                            </Box>
                            <Button
                                fullWidth variant="contained"
                                sx={TenantLoginStyles.primaryButton}
                                onClick={handleSubscribe}
                                disabled={loading}
                                startIcon={<CreditCardIcon />}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Subscribe Now — $5.99/mo'}
                            </Button>
                            <Button
                                fullWidth variant="outlined"
                                sx={TenantLoginStyles.secondaryButton}
                                onClick={() => setMode('email')}
                            >
                                Back
                            </Button>
                        </Box>
                    )}

                    {/* ── Verify code section ────────────────────────────────── */}
                    {mode === 'verify' && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                            {!isSubscriber && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Your property has an active subscription — you're good to go!
                                </Alert>
                            )}
                            <TextField
                                required fullWidth label="Access Code"
                                value={accessCode}
                                onChange={e => setAccessCode(e.target.value)}
                                sx={TenantLoginStyles.textField}
                                placeholder="Enter 4–10 digit code"
                                inputProps={{
                                    minLength: 4, maxLength: 10,
                                    style: { letterSpacing: '8px', fontSize: '24px', textAlign: 'center' }
                                }}
                            />
                            <Button
                                fullWidth variant="contained"
                                sx={TenantLoginStyles.primaryButton}
                                onClick={handleVerifyCode}
                                disabled={loading || accessCode.length < 4 || accessCode.length > 10}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
                            </Button>
                            <Button
                                fullWidth variant="outlined"
                                sx={TenantLoginStyles.secondaryButton}
                                onClick={() => setMode('email')}
                            >
                                Back
                            </Button>
                        </Box>
                    )}

                    <Divider sx={TenantLoginStyles.divider}>OR</Divider>
                    <Typography variant="body2" sx={TenantLoginStyles.linkText}>
                        Property manager?{' '}
                        <Link component={RouterLink} to="/tenant-access/login" sx={TenantLoginStyles.link}>
                            Login here
                        </Link>
                    </Typography>
                </Box>
            </Paper>
        </Grid>
    );
};

export default TenantLogin;